/**
 * SF-157 Component 2: combined key+direction sort options for Intel Hub's target-grouped list —
 * mirrors `dossierSort.ts`'s shipped `SORT_OPTIONS`/`compareDossierRows`/`sortDossierRows` shape
 * exactly (the design SF-156's own Phase 2 review already tried a bare-key `<select>` for and
 * reversed, for the identical reason: a primary "show me the weakest X first" query becomes
 * unreachable on a scouting/targeting page). Every metric offers both directions — no metric is
 * single-direction-only, which resolves Raw TPA's direction ambiguity by construction (the player
 * picks the direction they mean, labeled explicitly).
 *
 * The single owning module for target-sort logic — both `MobileIntelHubShell` and
 * `DesktopIntelHubShell` reduce to thin wiring against this module rather than each duplicating
 * (and separately under-testing) their own comparator.
 */

import type { TargetGroup } from './groupIntelByTarget';
import { deriveHeadlineStats, researchRateOrdinal } from './groupIntelByTarget';

export type SortDirection = 'asc' | 'desc';

export const TARGET_SORT_OPTIONS = [
  { value: 'freshest-desc', label: 'Freshest (Newest First)', direction: 'desc' },
  { value: 'networth-desc', label: 'Networth (High to Low)', direction: 'desc' },
  { value: 'networth-asc', label: 'Networth (Low to High)', direction: 'asc' },
  { value: 'land-desc', label: 'Land (High to Low)', direction: 'desc' },
  { value: 'land-asc', label: 'Land (Low to High)', direction: 'asc' },
  { value: 'offensePoints-desc', label: 'Offense (High to Low)', direction: 'desc' },
  { value: 'offensePoints-asc', label: 'Offense (Low to High)', direction: 'asc' },
  { value: 'defensePoints-desc', label: 'Defense (High to Low)', direction: 'desc' },
  { value: 'defensePoints-asc', label: 'Defense (Low to High)', direction: 'asc' },
  { value: 'gold-desc', label: 'Gold (High to Low)', direction: 'desc' },
  { value: 'gold-asc', label: 'Gold (Low to High)', direction: 'asc' },
  { value: 'rawTPA-desc', label: 'Raw TPA — Thievery Ease (High to Low)', direction: 'desc' },
  { value: 'rawTPA-asc', label: 'Raw TPA — Thievery Ease (Low to High)', direction: 'asc' },
  { value: 'researchRate-desc', label: 'Research Rate (High to Low)', direction: 'desc' },
  { value: 'researchRate-asc', label: 'Research Rate (Low to High)', direction: 'asc' },
  { value: 'homeDefense-desc', label: 'Home Defense (High to Low)', direction: 'desc' },
  { value: 'homeDefense-asc', label: 'Home Defense (Low to High)', direction: 'asc' },
] as const satisfies readonly { value: string; label: string; direction: SortDirection }[];

export type TargetSortValue = (typeof TARGET_SORT_OPTIONS)[number]['value'];

type Accessor = (group: TargetGroup) => number | null;

const ACCESSORS: Record<TargetSortValue, Accessor> = {
  'freshest-desc': (g) => g.freshestCapturedAt,
  'networth-desc': (g) => deriveHeadlineStats(g).networth,
  'networth-asc': (g) => deriveHeadlineStats(g).networth,
  'land-desc': (g) => deriveHeadlineStats(g).land,
  'land-asc': (g) => deriveHeadlineStats(g).land,
  'offensePoints-desc': (g) => deriveHeadlineStats(g).offensePoints,
  'offensePoints-asc': (g) => deriveHeadlineStats(g).offensePoints,
  'defensePoints-desc': (g) => deriveHeadlineStats(g).defensePoints,
  'defensePoints-asc': (g) => deriveHeadlineStats(g).defensePoints,
  'gold-desc': (g) => deriveHeadlineStats(g).gold,
  'gold-asc': (g) => deriveHeadlineStats(g).gold,
  'rawTPA-desc': (g) => deriveHeadlineStats(g).rawTPA,
  'rawTPA-asc': (g) => deriveHeadlineStats(g).rawTPA,
  'researchRate-desc': (g) => researchRateOrdinal(deriveHeadlineStats(g).researchRate),
  'researchRate-asc': (g) => researchRateOrdinal(deriveHeadlineStats(g).researchRate),
  'homeDefense-desc': (g) => deriveHeadlineStats(g).homeDefense,
  'homeDefense-asc': (g) => deriveHeadlineStats(g).homeDefense,
};

/** Null-last regardless of direction (mirrors dossierSort.ts's compareDossierRows exactly). */
export function compareTargetGroups(a: TargetGroup, b: TargetGroup, value: TargetSortValue): number {
  const option = TARGET_SORT_OPTIONS.find((o) => o.value === value);
  const accessor = ACCESSORS[value];
  if (!option || !accessor) return 0;

  const av = accessor(a);
  const bv = accessor(b);

  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;

  const cmp = av - bv;
  return option.direction === 'asc' ? cmp : -cmp;
}

/** Returns a new sorted array — never mutates `groups` (mirrors dossierSort.ts's sortDossierRows). */
export function sortTargetGroups(groups: TargetGroup[], value: TargetSortValue): TargetGroup[] {
  return [...groups].sort((a, b) => compareTargetGroups(a, b, value));
}
